import {
  PRODUCT_ID,
  type BundleMetadata,
  type DiagnosticsBreadcrumb,
  type DiagnosticsConsoleEntry,
  type DiagnosticsPayload,
  type DiagnosticsRequestId,
  type TruncationReport,
} from '@shared/support-bundles/contract'
import { capEntriesByBytes } from './caps'

interface Channel<T> {
  snapshot(): T[]
  droppedCount(): number
}

export interface CollectSources {
  console: Channel<DiagnosticsConsoleEntry>
  breadcrumbs: Channel<DiagnosticsBreadcrumb>
  requestIds: Channel<DiagnosticsRequestId>
  metadata: () => BundleMetadata
  now?: () => number
}

export interface CollectLimits {
  consoleBytes: number
  breadcrumbBytes: number
  requestIdBytes: number
}

const DEFAULT_LIMITS: CollectLimits = {
  consoleBytes: 1024 * 1024,
  breadcrumbBytes: 256 * 1024,
  requestIdBytes: 64 * 1024,
}

/**
 * Builds the diagnostics payload from the live capture channels.
 *
 * This is the "collect" step of collect → cap → redact → serialise → gzip. That
 * order is load-bearing: gzipping before redacting would make the redaction a
 * no-op on compressed bytes while every unit test asserting against the
 * intermediate object still passed.
 *
 * Each channel can lose entries twice — once to its own ring-buffer window, and
 * again to byte capping — and both losses are summed into one truncation count,
 * because triage cares how much is missing, not which mechanism dropped it.
 */
export function collectDiagnostics(
  sources: CollectSources,
  limits: CollectLimits = DEFAULT_LIMITS,
): DiagnosticsPayload {
  const now = sources.now ?? ((): number => Date.now())

  const consoleCapped = capEntriesByBytes(sources.console.snapshot(), limits.consoleBytes)
  const breadcrumbsCapped = capEntriesByBytes(
    sources.breadcrumbs.snapshot(),
    limits.breadcrumbBytes,
  )
  const requestIdsCapped = capEntriesByBytes(
    sources.requestIds.snapshot(),
    limits.requestIdBytes,
  )

  const truncation: TruncationReport = {}
  const consoleDropped = sources.console.droppedCount() + consoleCapped.dropped
  const breadcrumbsDropped = sources.breadcrumbs.droppedCount() + breadcrumbsCapped.dropped
  const requestIdsDropped = sources.requestIds.droppedCount() + requestIdsCapped.dropped
  if (consoleDropped > 0) truncation.console_messages = consoleDropped
  if (breadcrumbsDropped > 0) truncation.breadcrumbs = breadcrumbsDropped
  if (requestIdsDropped > 0) truncation.request_ids = requestIdsDropped

  return {
    source: PRODUCT_ID,
    client_ts: now(),
    console_messages: consoleCapped.kept,
    breadcrumbs: breadcrumbsCapped.kept,
    request_ids: requestIdsCapped.kept,
    metadata: sources.metadata(),
    truncation,
  }
}
